import { useQuery } from '@tanstack/react-query'
import { Input, Select } from 'antd'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Header from '@components/header'
import Layout from '@components/Layout'
import { useDebounce } from '@hooks/useDebounce'
import { downloadExcel } from '@utils/downloadPdf'
import { fetchProductionDeliveryReport, fetchDepartments } from '@utils/fetchData'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'

const DeliveryReport = () => {
	const { t } = useTranslation()
	const formattedDate = moment().format('YYYY-MM-DD')

	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState('')
	const [docNum, setDocNum] = useState('')
	const [department, setDepartment] = useState('')
	const [departmentName, setDepartmentName] = useState('')
	const [itemCode, setItemCode] = useState('')
	const [itemName, setItemName] = useState('')
	const [warehouse, setWarehouse] = useState('')

	const debouncedDocNum = useDebounce(docNum, 500)
	const debouncedDepartment = useDebounce(department, 500)
	const debouncedDepartmentName = useDebounce(departmentName, 500)
	const debouncedItemCode = useDebounce(itemCode, 500)
	const debouncedItemName = useDebounce(itemName, 500)

	const [pageSize] = useState(20)
	const [page, setPage] = useState(0)
	const [loadingExcel, setLoadingExcel] = useState(false)

	const { data: departmentData = [] } = useQuery({
		queryKey: ['departments'],
		queryFn: fetchDepartments,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'production-delivery-report',
			pageSize,
			page,
			startDate,
			endDate,
			debouncedDocNum,
			debouncedDepartment,
			debouncedDepartmentName,
			debouncedItemCode,
			debouncedItemName,
			warehouse,
		],
		queryFn: fetchProductionDeliveryReport,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const columns = [
		{
			title: t('docNum'),
			dataIndex: 'docNum',
			key: 'docNum',
		},
		{
			title: t('docDate'),
			dataIndex: 'docDate',
			key: 'docDate',
			render: (value) => value ? moment(value).format('DD.MM.YYYY') : '-',
		},

		{
			title: t('departmentName'),
			dataIndex: 'departmentName',
			key: 'departmentName',
		},
		{
			title: t('itemCode'),
			dataIndex: 'itemCode',
			key: 'itemCode',
		},
		{
			title: t('itemName'),
			dataIndex: 'itemName',
			key: 'itemName',
		},
		{
			title: t('deliveredQuantity'),
			dataIndex: 'deliveredQuantity',
			key: 'deliveredQuantity',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('deliveredPriceWithoutCosts'),
			dataIndex: 'deliveredPriceWithoutCosts',
			key: 'deliveredPriceWithoutCosts',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('deliveredPriceWithCosts'),
			dataIndex: 'deliveredPriceWithCosts',
			key: 'deliveredPriceWithCosts',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
	]

	const handleNextPage = () => {
		if (data.length === pageSize) {
			setPage((prev) => prev + 1)
		}
	}

	const handlePreviousPage = () => {
		if (page > 0) {
			setPage((prev) => prev - 1)
		}
	}

	const handleDownloadExcel = () => {
		downloadExcel({
			url: 'api/reports/production-delivery-report/export',
			params: {
				startDate,
				endDate,
				docNum: debouncedDocNum,
				department: debouncedDepartment,
				departmentName: debouncedDepartmentName,
				itemCode: debouncedItemCode,
				itemName: debouncedItemName,
				warehouse,
				skip: 0,
				pageSize: 1000,
			},
			fileName: t('deliveryReport'),
			onLoadingChange: setLoadingExcel,
		})
	}

	return (
		<Layout>
			<Header
				title={'deliveryReport'}
				columns={columns}
				fdata={data}
				loading={isLoading}
				hasMoreData={data.length === pageSize}
				currentPage={page + 1}
				handlePreviousPage={handlePreviousPage}
				handleNextPage={handleNextPage}
				formattedDate={formattedDate}
				loadingExcel={loadingExcel}
				handleDownloadExcel={handleDownloadExcel}
				isShowExcelBtn
				renderFilters={() => (
					<div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 items-end gap-4">
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('startDate')}</p>
							<Input
								type="date"
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
								placeholder={t('startDate')}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('endDate')}</p>
							<Input
								type="date"
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
								placeholder={t('endDate')}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('docNum')}</p>
							<Input
								value={docNum}
								onChange={(e) => setDocNum(e.target.value)}
								placeholder={t('docNum')}
								allowClear
							/>
						</div>

						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('department')}</p>

									<Select
																value={department}
																style={{ color: 'black' }}
																onChange={(val) => {
																	setDepartment(val)
																}}
																options={departmentData.map(
																	(department) => {
																		return {
																			label: department.name,
																			value: department.code,
																		}
																	},
																)}
																			filterOption={(inputValue, option) =>
																		option.label
																			.toLowerCase()
																			.includes(inputValue.toLowerCase())
																	}
																	showSearch
																	allowClear
															/>
						</div>

						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('itemCode')}</p>
							<Input
								value={itemCode}
								onChange={(e) => setItemCode(e.target.value)}
								placeholder={t('itemCode')}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-1">
							<p className="font-nunito">{t('itemName')}</p>
							<Input
								value={itemName}
								onChange={(e) => setItemName(e.target.value)}
								placeholder={t('itemName')}
								allowClear
							/>
						</div>

					</div>
				)}
			/>
		</Layout>
	)
}

export default DeliveryReport
